'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import type { ExecutionsSelectAllState } from './executions-list.model';

export type ExecutionsTableContextValue = {
  selectAllState: ExecutionsSelectAllState;
  hasSelectableRows: boolean;
  isSelectionLocked: boolean;
  isTraceSelected: (trace: TracePreviewDto) => boolean;
  onToggleTraceSelected: ({
    trace,
    selected,
  }: {
    trace: TracePreviewDto;
    selected: boolean;
  }) => void;
  onSelectAll: (selected: boolean) => void;
  resumingTraceIds: ReadonlySet<string>;
  rerunningTraceIds: ReadonlySet<string>;
  abortingTraceIds: ReadonlySet<string>;
  onResumeTrace: (trace: TracePreviewDto) => void;
  onRerunTrace: (trace: TracePreviewDto) => void;
  onAbortTrace: (trace: TracePreviewDto) => void;
  onDeleteTrace: (trace: TracePreviewDto) => void;
};

const ExecutionsTableContext =
  createContext<ExecutionsTableContextValue | null>(null);

export function ExecutionsTableContextProvider({
  value,
  children,
}: {
  value: ExecutionsTableContextValue;
  children: ReactNode;
}) {
  return (
    <ExecutionsTableContext.Provider value={value}>
      {children}
    </ExecutionsTableContext.Provider>
  );
}

export function useExecutionsTableContext() {
  const value = useContext(ExecutionsTableContext);

  if (!value) {
    throw new Error(
      'useExecutionsTableContext must be used inside ExecutionsTableContextProvider',
    );
  }

  return value;
}
